import { computed } from 'vue'
import type { ComputedRef } from 'vue'
// types
import type { StrapiSchemaSettings, StrapiSiteSeo } from '@ts-strapi/interfaces'
import type { JsonLdNode } from '@ts-structured-data/types'
// helpers
import { resolveStrapiMediaUrl } from '@helpers/strapi-media'
import { trimTrailingSlash } from '@helpers/url'
// builders
import { buildOrganization } from './builders/build-organization'
import { buildWebsite } from './builders/build-website'

export const useSiteSeoHead = (
  siteSeo: ComputedRef<StrapiSiteSeo | null>,
  schemaSettings?: ComputedRef<StrapiSchemaSettings | null>,
): void => {
  const { strapi, wpOriginUrl } = useRuntimeConfig().public
  const refOrigin = computed<string>(() => trimTrailingSlash(wpOriginUrl))

  const refJsonLd = computed<string>(() => {
    const org = buildOrganization({
      siteSeo: siteSeo.value,
      settings: schemaSettings?.value ?? null,
      origin: refOrigin.value,
      strapiPublicUrl: strapi.publicUrl,
    })
    const website = buildWebsite({ siteSeo: siteSeo.value, origin: refOrigin.value })

    const graph = [org, website].filter((node): node is JsonLdNode => node !== null)
    if (!graph.length) return ''

    return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
  })

  useHead({
    script: [{ type: 'application/ld+json', innerHTML: refJsonLd, key: 'ld-site' }],
    meta: [
      { name: 'facebook-domain-verification', content: () => siteSeo.value?.facebookDomainVerification || '' },
      { name: 'msvalidate.01', content: () => siteSeo.value?.bingMsValidate || '' },
      { name: 'p:domain_verify', content: () => siteSeo.value?.pinterestDomainVerify || '' },
      {
        name: 'msapplication-TileImage',
        content: () => {
          const img = siteSeo.value?.msTileImage
          return img ? resolveStrapiMediaUrl(img.url, strapi.publicUrl) : ''
        },
      },
    ],
  })
}
